import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HttpErrorResponse } from '@angular/common/http';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { ConnectorService } from '../../../shared/services/connector.service';
import { AdmPricePolicyTypeaheadComponent } from 'src/app/shared/components/adm-price-policy-typeahead/adm-price-policy-typeahead.component';
import { PricePolicyType, PricePolicy } from '../../prices/price-policies/interfaces/price-policy';
import { LocationDetailsConnector } from '../charging-station-location-details/charging-station-locations-details.model';
import { DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE } from '../charging-station-location-model';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';

export interface ConnectorPricePolicyDialogData {
  organizationId: number;
  connector: LocationDetailsConnector;
  pricePolicyType: PricePolicyType;
  canHaveDynamicPricePolicy: boolean;
}

interface ConnectorPricePolicyDialogForm {
  pricePolicyId: FormControl<number | null>;
}

@Component({
  selector: 'adm-connector-price-policy-dialog',
  templateUrl: './connector-price-policy-dialog.component.html',
  styleUrls: ['./connector-price-policy-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    AdmPricePolicyTypeaheadComponent,
    FeedbackInfoComponent,
  ],
})
export class ConnectorPricePolicyDialogComponent {
  dialogRef = inject<MatDialogRef<ConnectorPricePolicyDialogComponent>>(MatDialogRef);
  private snackbarService = inject(SnackbarService);
  private connectorService = inject(ConnectorService);
  data = inject<ConnectorPricePolicyDialogData>(MAT_DIALOG_DATA);

  private readonly validateNetAreaForPricePolicy = toSignal(
    inject(FeatureFlagService)
      .getFeatureFlag(FEATURE_FLAGS.VALIDATE_NET_AREA_FOR_PRICE_POLICY)
      .pipe(map(flag => flag.result)),
    { initialValue: false }
  );

  public readonly dynamicPricePolicyWarning = DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE;
  public loading = false;
  public selectedPricePolicy: PricePolicy | null = null;
  private connector: LocationDetailsConnector = this.data.connector;
  public currentPricePolicyId: number | null = this.connector?.pricePolicyId ?? null;

  public formGroup: FormGroup<ConnectorPricePolicyDialogForm> = new FormGroup<ConnectorPricePolicyDialogForm>({
    pricePolicyId: new FormControl<number | null>(this.data?.connector?.pricePolicyId ?? null, Validators.required),
  });

  get canHaveDynamicPricePolicy(): boolean {
    return !this.validateNetAreaForPricePolicy() || this.data.canHaveDynamicPricePolicy === true;
  }

  get pricePolicyId(): FormControl<number | null> {
    return this.formGroup.controls.pricePolicyId;
  }

  onPricePolicySelected(pricePolicy: PricePolicy | null): void {
    this.selectedPricePolicy = pricePolicy;
    this.pricePolicyId.setValue(pricePolicy?.id ?? null);
  }

  public close() {
    this.dialogRef.close();
  }

  savePricePolicy(): void {
    if (!this.canHaveDynamicPricePolicy && this.selectedPricePolicy?.type === PricePolicyType.Dynamic) {
      this.snackbarService.warning(this.dynamicPricePolicyWarning);
      return;
    }

    const selectedPricePolicyId = this.pricePolicyId.value;
    if (selectedPricePolicyId == null) {
      return;
    }

    this.loading = true;
    this.connectorService
      .setPricePolicy(selectedPricePolicyId.toString(), this.data.connector.identifier)
      .subscribe({
        next: () => {
          this.connector.pricePolicyName = this.selectedPricePolicy?.name ?? this.connector.pricePolicyName;
          this.connector.consumerPriceInclVat =
            this.selectedPricePolicy?.consumerPriceInKrWithVat.fixed?.value ?? this.connector.consumerPriceInclVat;
          this.connector.pricePolicyId = selectedPricePolicyId;
          this.dialogRef.close(this.connector);
        },
        error: (httpError: HttpErrorResponse) => {
          if (httpError.error?.code === 100613) {
            this.snackbarService.error(
              'Den valgte prispolitik passer ikke til den valgte OK-måler-indstilling. Vælg en anden prispolitik og prøv igen.'
            );
          } else if (httpError.error?.code === 100620) {
            this.snackbarService.error(this.dynamicPricePolicyWarning);
          } else {
            this.snackbarService.error('Der skete en fejl under opdatering af prispolitik.');
          }
        },
      })
      .add(() => {
        this.loading = false;
      });
  }
}
